'use client';

import type { NavItem, NavItems } from '@/types/nav';
import { MenuItems } from '@/hooks/query/use-menu-data';
import { useSportMenuActived } from '@/hooks/use-sport-menu-actived';
import { SimpleAccordion, SimpleAccordionItem } from '@nf/ui';
import { SidebarItem } from '../sidebar-item';
import { useCurrentProperties } from '@/hooks/query/use-menu-data';
import { isDummySportCategory } from '@/services/settings.service';
import { sportService } from '@/services/sport.service';
import { useState, useEffect } from 'react';
import { uiService } from '@/services/ui.service';
import { matchSome } from '@nf/utils-common/fpw/monadic';
import { useSiteStore } from '@/store/site';
import { sidebarStyle } from '../../sidebar.style';
import { ViewEnum } from '@/types';
import clsx from 'clsx';
import { useAppearanceStore } from '@/store/appearance';

type SidebarSportsMenuProps = {
	data: NavItems;
	isShowMore?: boolean;
}

export const useMenuItemToNavItemMapper = (): ((menuItem: MenuItems, index: number) => NavItem) => {
	const { isSportActived, isActivedByLeagueGroupId, isCricketGameTypeActived, isVirtualSportActived, isDummySportCategoryActived } = useSportMenuActived();
	const selectedProductPage = useSiteStore(state => state.currentPage);
	
	const hasStreamerSportId = ['1', '2', '6'];
	
	return (item, idx): NavItem => {
		const isAnyVirtualSportsProduct = sportService.isAnyVirtualSportsProduct(Number(item.gameId));

		return {
			sportId: item.gameId,
			href: item.href,
			productName: isAnyVirtualSportsProduct ? 'virtualSports' : undefined,
			className: 'side-menu__btn',
			icon: item.icon,
			iconType: 'sport',
			type: item.subMenu ? 'collapsible' : 'flexible',
			title: item.name,
			amount: item.matchCount,
			hasStreamer: hasStreamerSportId.includes(item.gameId),
			hasLive: item.hasLive,
			menuSource: item.gameId === '134'
				? 'sports'
				: isAnyVirtualSportsProduct
					? 'virtual'
					: 'sports',
			selected: matchSome(Number(item.gameId))
				.on(
					() => isAnyVirtualSportsProduct && !!item.subMenu,
					() => isVirtualSportActived() && selectedProductPage === 'lbl_VirtualSport'
				)
				.on(isDummySportCategory, isDummySportCategoryActived)
				.otherwise(() => item.gameId === '134'
					? isSportActived(item.href) && selectedProductPage !== 'lbl_VirtualSport'
					: isSportActived(item.href)),
			subItems: !item.subMenu ? undefined : item.subMenu.map(menuItem => ({
				sportId: menuItem.gameId,
				href: menuItem.href,
				className: 'side-sub-menu__btn',
				icon: menuItem.icon,
				iconType: 'sport',
				type: 'collapsible',
				title: menuItem.name,
				amount: menuItem.matchCount,
				menuSource: isAnyVirtualSportsProduct ? 'virtual' : 'sports',
				selected: item.gameId === '43'
					? isActivedByLeagueGroupId(menuItem.href)
					: item.gameId === '50'
						? isCricketGameTypeActived(menuItem.href, idx)
						: item.gameId === '116' && menuItem.gameId === '134'
							? isSportActived(menuItem.href) && selectedProductPage === 'lbl_VirtualSport'
							: isSportActived(menuItem.href)
			}))
		};
	};
};

export const SidebarSportsMenu = ({ data }: SidebarSportsMenuProps) => {
	const { routePrefix, sport } = useCurrentProperties();
	const [enableClickThenNavigate, setEnableClickThenNavigate] = useState(false);
	const view = useAppearanceStore(state => state.view);

	useEffect(() => {
		const subscription = uiService.onControlSidebarButton$.subscribe(isWidthUnder750 => {
			setEnableClickThenNavigate(!isWidthUnder750);
		});

		return () => {
			subscription.unsubscribe();
		};
	}, []);

	return data.map((item, idx) => {
		if (item.sportId === '999') return
		return !item.subItems ? (
			<SidebarItem
				key={idx}
				item={item}
			>
				{item.title}
			</SidebarItem>
		) : (
			<SimpleAccordion
				key={idx}
				defaultValue={routePrefix === 'live'
					? `/${routePrefix}/${sport}`
					: sportService.isAnyVirtualSportsProduct(Number(sport))
						? `/${routePrefix}/${sport}`
						: `/${routePrefix}/${sport}/t`
				}
			>
				<SimpleAccordionItem
					className={clsx('side-menu__item', { [sidebarStyle.sideMenuItem]: view === ViewEnum.mars })}
					value={item.href}
					hasSummary={false}
					isSelected={item.selected}
				>
					<SidebarItem clickThenNavigate={enableClickThenNavigate} item={item}>
						{item.title}
					</SidebarItem>
				</SimpleAccordionItem>
			</SimpleAccordion>
		);
	});
};
